import React, { useState, useEffect } from 'react';
import { Box, HStack, Text, VStack, useMediaQuery, Badge, Divider, Pressable } from 'native-base';
import Chart from 'react-apexcharts';
import { Svg } from 'react-native-svg';
import { COLORS } from '../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../utils/screens';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../utils/fonts';
import NoDataFound from '../components/noDataFound';
import { Toaster } from '../components/toaster';
import { LoaderAnimation } from '../components/spinner';
import { downloadIconSVG } from '../utils/svg';
import { downloadSurveyReportAPI } from '../api/apis/quicklinks';

interface SurveyPieChartsProps {
    data: any;
    activeTab: string;
    MobileWindow: boolean;
}

const SurveyPieCharts: React.FC<SurveyPieChartsProps> = ({
    data,
    activeTab,
    MobileWindow
}) => {
    const [processedSurveys, setProcessedSurveys] = useState<any[]>([]);
    const toaster = Toaster();

    useEffect(() => {
        if (data && data?.data) {
            processSurveyData(data?.data);
        }
    }, [data]);

    const processSurveyData = (surveyData: any[]) => {
        if (!surveyData || surveyData?.length === 0) return;

        const processed = surveyData?.map(survey => {
            const chartData = processSurveyStatistics(survey);
            return {
                ...survey,
                chartData
            };
        });

        setProcessedSurveys(processed);
    };

    const processSurveyStatistics = (survey: any) => {
        // If no responses, show empty state
        if (!survey?.statistics || survey?.statistics?.total_responses === 0) {
            return {
                labels: ['No Responses'],
                series: [100],
                colors: ['#e0e0e0'],
                responseWeights: [0]
            };
        }

        const labels: string[] = [];
        const series: number[] = [];
        const colors: string[] = [];
        const responseWeights: number[] = [];

        // Process questions based on question_statistics
        if (survey?.statistics?.question_statistics && survey?.statistics?.question_statistics?.length > 0) {
            survey?.statistics?.question_statistics?.forEach((questionStat: any, qIndex: number) => {
                // Try both approaches to find the question
                const questionIndex = parseInt(questionStat?.question_id) - 1;
                let question = survey?.questions?.[questionIndex];
                
                // If not found by index, try by question_id
                if (!question) {
                    question = survey?.questions?.find((q: any) => q?.question_id === parseInt(questionStat?.question_id));
                }
                
                if (question) {
                    labels.push(question?.question);
                    series.push(questionStat?.response_weight_percent || 0);
                    colors.push(getRandomColor());
                    responseWeights.push(questionStat?.response_weight_percent || 0);
                }
            });
        } else if (survey?.questions && survey?.questions?.length > 0) {
            // Fallback: if no question_statistics, show questions with equal distribution
            survey?.questions?.forEach((question: any, qIndex: number) => {
                labels.push(question?.question);
                const equalWeight = 100 / survey?.questions?.length;
                series.push(equalWeight);
                colors.push(getRandomColor());
                responseWeights.push(equalWeight);
            });
        }

        // If no questions, show total responses
        if (labels.length === 0) {
            labels.push('Total Responses');
            series.push(survey?.statistics?.total_responses || 0);
            colors.push(getRandomColor());
            responseWeights.push(100);
        }

        return {
            labels,
            series,
            colors,
            responseWeights
        };
    };

    const getRandomColor = () => {
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
            '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
            '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const getQuestionType = (question: any) => {
        if (question?.options?.length === 2 &&
            question?.options?.includes('True') &&
            question?.options?.includes('False')) {
            return 'True/False';
        }

        if (question?.options?.length === 5 &&
            question?.options?.every((opt: string, i: number) =>
                opt.includes(`${i + 1} Star`) || opt.includes(`${i + 1} Stars`))) {
            return 'Rating';
        }

        return 'Input Field';
    };

    const getSurveyTypeColor = (type: string) => {
        return type === 'event' ? 'blue' : 'green';
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const downloadSurveyReport = async (surveyId: string, surveyTitle: string) => {
        try {
            LoaderAnimation.show();

            const response = await downloadSurveyReportAPI(surveyId);
            // Generate default filename
            let filename = `${surveyTitle}_survey_report.xlsx`;

            if (response?.filename) {
                filename = response?.filename;
            }
            // Create download URL from blob
            const url = window.URL.createObjectURL(response.data);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Clean up
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Show success toaster
            toaster('Survey Report downloaded successfully!', 'success');
        } catch (error) {
            toaster(
                error.message || 'Failed to download survey report. Please try again later.',
                'error'
            );
        } finally {
            LoaderAnimation.hide();
        }
    };

    if (!data || !data?.data || data?.data?.length === 0) {
        return <NoDataFound dataName="Survey responses" />;
    }

    return (
        <VStack space={6}>
            <Box>
                <Text
                    fontSize={MobileWindow ? '14px' : '16px'}
                    color="gray.600"
                    mb={6}
                >
                    Visual representation of survey responses with detailed breakdowns
                </Text>
            </Box>

            <VStack space={6}>
                {processedSurveys.map((survey, index) => (
                    <Box
                        key={survey.survey_id}
                        bg="white"
                        borderRadius="xl"
                        shadow="lg"
                        p={6}
                        borderWidth={1}
                        borderColor="gray.100"
                    >
                        {/* Survey Header */}
                        <VStack space={4} mb={6}>
                            <HStack justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                                <VStack flex={1} space={2}>
                                    <HStack space={3} alignItems="center" flexWrap="wrap">
                                        <Text
                                            fontSize={MobileWindow ? '18px' : '22px'}
                                            fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                                            color={COLORS.PARTNERS_HEADING_COLOR}
                                        >
                                            {survey.title}
                                        </Text>
                                        <Badge
                                            colorScheme={getSurveyTypeColor(survey.type)}
                                            variant="subtle"
                                            rounded="full"
                                            px={3}
                                        >
                                            {survey.type === 'event' ? 'Event' : 'MICE'}
                                        </Badge>
                                    </HStack>

                                    <HStack space={4} alignItems="center" flexWrap="wrap">
                                        <Text fontSize="md" color="gray.600">
                                            Created on: {formatDate(survey.created_at)}
                                        </Text>
                                    </HStack>
                                </VStack>
                                {/* Download Icon */}
                                <Pressable onPress={() => downloadSurveyReport(survey?.survey_id, survey?.title)}>
                                    <Box p={2}>
                                        <Svg
                                            width={MobileWindow ? '28' : '32'}
                                            height={MobileWindow ? '28' : '32'}
                                            viewBox='0 0 24 24'
                                        >
                                            {downloadIconSVG}
                                        </Svg>
                                    </Box>
                                </Pressable>
                            </HStack>
                        </VStack>

                        {/* Survey Statistics */}
                        {survey?.statistics && (
                            <Box>
                                <Text
                                    fontSize="xl"
                                    fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                                    color={COLORS.PARTNERS_HEADING_COLOR}
                                    mb={4}
                                >
                                    Survey Statistics
                                </Text>
                                <VStack space={3}>
                                    <HStack space={4} flexWrap="wrap">
                                        <Box bg="blue.50" p={3} borderRadius="md" flex={1} minW="150px">
                                            <Text fontSize="md" color="blue.600" fontWeight="medium">
                                                Total Responses
                                            </Text>
                                            <Text fontSize="xl" fontWeight="bold" color="blue.700">
                                                {survey?.statistics?.total_responses || 0}
                                            </Text>
                                        </Box>
                                        <Box bg="green.50" p={3} borderRadius="md" flex={1} minW="150px">
                                            <Text fontSize="md" color="green.600" fontWeight="medium">
                                                Completion Rate
                                            </Text>
                                            <Text fontSize="xl" fontWeight="bold" color="green.700">
                                                {survey?.statistics?.completion_rate || 0}%
                                            </Text>
                                        </Box>
                                        <Box bg="purple.50" p={3} borderRadius="md" flex={1} minW="150px">
                                            <Text fontSize="md" color="purple.600" fontWeight="medium">
                                                Questions
                                            </Text>
                                            <Text fontSize="xl" fontWeight="bold" color="purple.700">
                                                {survey?.questions?.length || 0}
                                            </Text>
                                        </Box>
                                    </HStack>
                                </VStack>
                            </Box>
                        )}

                        {/* Chart Section */}
                        <Box mt={6}>
                            <Text
                                fontSize="xl"
                                fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                                color={COLORS.PARTNERS_HEADING_COLOR}
                                mb={4}
                            >
                                Response Distribution
                            </Text>

                            <Box
                                bg="gray.50"
                                borderRadius="lg"
                                p={8}
                                // minHeight="800px"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                {survey?.chartData?.labels?.length > 0 && survey?.chartData?.labels[0] !== 'No Responses' ? (
                                    <HStack space={6} alignItems="flex-start" width="100%">
                                        {/* Pie Chart */}
                                        <Box flex={MobileWindow ? 1 : 2}>
                                            <Chart
                                                options={{
                                                    chart: {
                                                        type: 'pie',
                                                        toolbar: {
                                                            show: false
                                                        }
                                                    },
                                                    labels: survey?.chartData?.labels,
                                                    colors: survey?.chartData?.colors,
                                                    tooltip: {
                                                        enabled: true,
                                                        y: {
                                                            formatter: function (val: number, { dataPointIndex }) {
                                                                const responseWeight = survey?.chartData?.responseWeights?.[dataPointIndex] || 0;
                                                                return `${val.toFixed(1)}% (Response Weight: ${responseWeight.toFixed(1)}%)`;
                                                            }
                                                        }
                                                    },
                                                    dataLabels: {
                                                        enabled: false
                                                    },
                                                    legend: {
                                                        show: false
                                                    },
                                                    plotOptions: {
                                                        pie: {
                                                            donut: {
                                                                size: '30%'
                                                            }
                                                        }
                                                    }
                                                }}
                                                series={survey?.chartData?.series}
                                                type="pie"
                                                height={MobileWindow ? 300 : 350}
                                            />
                                        </Box>

                                        {/* Response Users Legend */}
                                        {survey?.statistics?.response_user_email && survey?.statistics?.response_user_email?.length > 0 && (
                                            <Box 
                                                flex={1} 
                                                minW={MobileWindow ? "200px" : "250px"}
                                                bg="white"
                                                borderRadius="lg"
                                                p={4}
                                                shadow="sm"
                                                borderWidth={1}
                                                borderColor="gray.200"
                                            >
                                                <Text 
                                                    fontSize="lg" 
                                                    fontWeight="bold" 
                                                    color="gray.700" 
                                                    mb={4}
                                                    textAlign="center"
                                                >
                                                    Response Users
                                                </Text>
                                                <VStack space={3}>
                                                    {survey?.statistics?.response_user_email?.map((email: string, emailIndex: number) => (
                                                        <HStack key={emailIndex} space={3} alignItems="center">
                                                            <Box
                                                                width="8px"
                                                                height="8px"
                                                                borderRadius="full"
                                                                bg={COLORS.THEME_COLOR}
                                                            />
                                                            <Text 
                                                                fontSize="sm" 
                                                                color="gray.600" 
                                                                flex={1}
                                                                numberOfLines={1}
                                                            >
                                                                {email}
                                                            </Text>
                                                        </HStack>
                                                    ))}
                                                </VStack>
                                                
                                                {/* Total Users Count */}
                                                <Box 
                                                    mt={4} 
                                                    pt={3} 
                                                    borderTopWidth={1} 
                                                    borderTopColor="gray.200"
                                                >
                                                    <Text 
                                                        fontSize="sm" 
                                                        fontWeight="semibold" 
                                                        color="gray.700"
                                                        textAlign="center"
                                                    >
                                                        Total Users: {survey?.statistics?.response_user_email?.length}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        )}
                                    </HStack>
                                ) : (
                                    <VStack space={3} alignItems="center">
                                        <Text fontSize="lg" color="gray.500" fontWeight="medium">
                                            No Responses Yet
                                        </Text>
                                        <Text fontSize="md" color="gray.400" textAlign="center">
                                            This survey hasn't received any responses yet.
                                        </Text>
                                    </VStack>
                                )}
                            </Box>
                        </Box>


                        {/* Response Trends */}
                        {survey?.statistics && survey?.statistics?.response_trends && (
                            <Box mt={6}>
                                <Text
                                    fontSize="xl"
                                    fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                                    color={COLORS.PARTNERS_HEADING_COLOR}
                                    mb={4}
                                >
                                    Response Trends
                                </Text>
                                <VStack space={4}>
                                    <HStack space={4} flexWrap="wrap">
                                        <Box 
                                            bg="white" 
                                            p={4} 
                                            borderRadius="xl" 
                                            flex={1} 
                                            minW="180px"
                                            shadow="md"
                                            borderWidth={1}
                                            borderColor="orange.200"
                                            position="relative"
                                            overflow="hidden"
                                        >
                                            <Box 
                                                position="absolute" 
                                                top={0} 
                                                left={0} 
                                                right={0} 
                                                height="4px" 
                                                bg="orange.400"
                                            />
                                            <VStack space={2}>
                                                <HStack space={2} alignItems="center">
                                                    <Box 
                                                        bg="orange.100" 
                                                        p={2} 
                                                        borderRadius="full"
                                                        width="40px"
                                                        height="40px"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                    >
                                                        <Text fontSize="lg" color="orange.600" fontWeight="bold">📅</Text>
                                                    </Box>
                                                    <VStack flex={1}>
                                                        <Text fontSize="sm" color="gray.500" fontWeight="medium"  letterSpacing="wide">
                                                            Peak Response Date
                                                        </Text>
                                                        <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                                            {survey?.statistics?.response_trends?.peak_response_date || 'N/A'}
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </VStack>
                                        </Box>

                                        <Box 
                                            bg="white" 
                                            p={4} 
                                            borderRadius="xl" 
                                            flex={1} 
                                            minW="180px"
                                            shadow="md"
                                            borderWidth={1}
                                            borderColor="teal.200"
                                            position="relative"
                                            overflow="hidden"
                                        >
                                            <Box 
                                                position="absolute" 
                                                top={0} 
                                                left={0} 
                                                right={0} 
                                                height="4px" 
                                                bg="teal.400"
                                            />
                                            <VStack space={2}>
                                                <HStack space={2} alignItems="center">
                                                    <Box 
                                                        bg="teal.100" 
                                                        p={2} 
                                                        borderRadius="full"
                                                        width="40px"
                                                        height="40px"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                    >
                                                        <Text fontSize="lg" color="teal.600" fontWeight="bold">📊</Text>
                                                    </Box>
                                                    <VStack flex={1}>
                                                        <Text fontSize="sm" color="gray.500" fontWeight="medium" letterSpacing="wide">
                                                            Days Active
                                                        </Text>
                                                        <Text fontSize="xl" fontWeight="bold" color="gray.800">
                                                            {survey?.statistics?.response_trends?.total_days_active || 0}
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </VStack>
                                        </Box>

                                        <Box 
                                            bg="white" 
                                            p={4} 
                                            borderRadius="xl" 
                                            flex={1} 
                                            minW="180px"
                                            shadow="md"
                                            borderWidth={1}
                                            borderColor="pink.200"
                                            position="relative"
                                            overflow="hidden"
                                        >
                                            <Box 
                                                position="absolute" 
                                                top={0} 
                                                left={0} 
                                                right={0} 
                                                height="4px" 
                                                bg="pink.400"
                                            />
                                            <VStack space={2}>
                                                <HStack space={2} alignItems="center">
                                                    <Box 
                                                        bg="pink.100" 
                                                        p={2} 
                                                        borderRadius="full"
                                                        width="40px"
                                                        height="40px"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                    >
                                                        <Text fontSize="lg" color="pink.600" fontWeight="bold">⭐</Text>
                                                    </Box>
                                                    <VStack flex={1}>
                                                        <Text fontSize="sm" color="gray.500" fontWeight="medium"  letterSpacing="wide">
                                                            Highest Scoring Questions
                                                        </Text>
                                                        <Text fontSize="sm" fontWeight="bold" color="gray.800" numberOfLines={2}>
                                                            {survey?.statistics?.summary?.highest_scoring_question || 'N/A'}
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </VStack>
                                        </Box>

                                        <Box 
                                            bg="white" 
                                            p={4} 
                                            borderRadius="xl" 
                                            flex={1} 
                                            minW="180px"
                                            shadow="md"
                                            borderWidth={1}
                                            borderColor="indigo.200"
                                            position="relative"
                                            overflow="hidden"
                                        >
                                            <Box 
                                                position="absolute" 
                                                top={0} 
                                                left={0} 
                                                right={0} 
                                                height="4px" 
                                                bg="indigo.400"
                                            />
                                            <VStack space={2}>
                                                <HStack space={2} alignItems="center">
                                                    <Box 
                                                        bg="indigo.100" 
                                                        p={2} 
                                                        borderRadius="full"
                                                        width="40px"
                                                        height="40px"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                    >
                                                        <Text fontSize="lg" color="indigo.600" fontWeight="bold">📉</Text>
                                                    </Box>
                                                    <VStack flex={1}>
                                                        <Text fontSize="sm" color="gray.500" fontWeight="medium"  letterSpacing="wide">
                                                            Lowest Scoring Questions
                                                        </Text>
                                                        <Text fontSize="sm" fontWeight="bold" color="gray.800" numberOfLines={2}>
                                                            {survey?.statistics?.summary?.lowest_response_rate_question || 'N/A'}
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </VStack>
                                        </Box>
                                    </HStack>
                                </VStack>
                            </Box>
                        )}

                        {/* Questions Summary */}
                        {survey?.questions && survey?.questions?.length > 0 && (
                            <Box mt={6}>
                                <Text
                                    fontSize="xl"
                                    fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                                    color={COLORS.PARTNERS_HEADING_COLOR}
                                    mb={4}
                                >
                                    Questions Overview
                                </Text>
                                <VStack space={3}>
                                    {survey?.questions?.map((question: any, qIndex: number) => (
                                        <Box
                                            key={qIndex}
                                            bg="gray.50"
                                            borderRadius="md"
                                            p={4}
                                            borderLeftWidth={4}
                                            borderLeftColor={getQuestionType(question) === 'Rating' ? 'purple.400' :
                                                getQuestionType(question) === 'True/False' ? 'orange.400' :
                                                    getQuestionType(question) === 'Input Field' ? 'blue.400' : 'gray.400'}
                                        >
                                            <VStack space={2}>
                                                <HStack justifyContent="space-between" alignItems="flex-start">
                                                    <Text fontWeight="semibold" fontSize="md" flex={1}>
                                                        Q{qIndex + 1}: {question?.question}
                                                    </Text>
                                                    <Badge
                                                        colorScheme={
                                                            getQuestionType(question) === 'Rating' ? 'purple' :
                                                                getQuestionType(question) === 'True/False' ? 'orange' :
                                                                    getQuestionType(question) === 'Input Field' ? 'blue' : 'gray'
                                                        }
                                                        variant="subtle"
                                                        size="sm"
                                                    >
                                                        {getQuestionType(question)}
                                                    </Badge>
                                                </HStack>
                                                <Text fontSize="sm" color="gray.600">
                                                    Options: {question?.options?.join(', ') || 'Text input'}
                                                </Text>
                                            </VStack>
                                        </Box>
                                    ))}
                                </VStack>
                            </Box>
                        )}
                    </Box>
                ))}
            </VStack>
        </VStack>
    );
};

export default SurveyPieCharts; 